import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Upload, Download, FileText, Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth, authHeaders } from "../../lib/auth";
import StatusPill from "../dashboard/StatusPill";

const BLANK = {
  overall_assessment: "",
  going_well: "",
  needs_improving: "",
  recommendations: "",
  top_priorities: ["", "", ""],
  additional_notes: "",
};

export default function CoachReview() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/coach/orders/${orderId}`, { headers: authHeaders(token) });
      setOrder(r.data);
      const src = r.data.review_content || r.data.review_draft;
      if (src) {
        setForm({
          ...BLANK,
          ...src,
          top_priorities: (src.top_priorities && src.top_priorities.length) ? [...src.top_priorities, "", "", ""].slice(0, 3) : ["", "", ""],
        });
      }
    } catch (e) {
      toast.error("Order not found.");
      nav("/coach");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId, token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPriority = (i, v) => setForm((f) => { const p = [...f.top_priorities]; p[i] = v; return { ...f, top_priorities: p }; });

  const buildPayload = () => ({
    ...form,
    top_priorities: form.top_priorities.map((s) => (s || "").trim()).filter(Boolean),
  });

  const saveDraft = async () => {
    setSaving(true);
    try { await api.put(`/coach/orders/${orderId}/review/draft`, buildPayload(), { headers: authHeaders(token) }); toast.success("Draft saved."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const attach = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/coach/orders/${orderId}/review/attach`, fd, { headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded."); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.overall_assessment.trim()) { toast.error("Overall Assessment is required."); return; }
    if (!window.confirm("Submit this review to the customer? This cannot be undone.")) return;
    setSubmitting(true);
    try {
      await api.post(`/coach/orders/${orderId}/review/submit`, buildPayload(), { headers: authHeaders(token) });
      toast.success("Review submitted.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed."); }
    finally { setSubmitting(false); }
  };

  const downloadCustomerFile = (filename, original) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/coach/orders/${orderId}/files/${filename}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = original || filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed."));
  };

  if (!order) return <div className="p-12 text-white/40">Loading…</div>;
  const completed = order.workspace_status === "completed";
  const files = order.customer_files || [];

  return (
    <div className="px-6 md:px-12 py-10 max-w-5xl" data-testid={`coach-review-${order.id}`}>
      <button onClick={() => nav("/coach")} className="text-[11px] tracking-[0.28em] uppercase text-white/50 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Back</button>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{order.id.slice(0, 8)} · {new Date(order.created_at).toLocaleDateString()}</div>
          <h1 className="font-anton uppercase text-4xl md:text-5xl mt-1 leading-[0.95]" data-testid="coach-review-title">{order.service_title}</h1>
          <div className="mt-2 flex flex-wrap gap-3 text-[10px] tracking-[0.22em] uppercase text-white/60">
            <span>Customer · {order.customer_first_name || "—"}</span>
            <span>{order.is_express ? "Express" : "Standard"}</span>
            <span>Payout · £{order.coach_payout?.toFixed(2)}</span>
          </div>
        </div>
        <StatusPill status={order.workspace_status} />
      </div>

      {/* Customer submission */}
      <section className="mt-10 border border-white/10 p-6" data-testid="coach-review-submission">
        <h2 className="font-anton uppercase text-2xl mb-4">Customer submission</h2>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">Notes</div>
        <p className="text-white/80 whitespace-pre-wrap min-h-[2rem]">{order.customer_notes || "— No notes —"}</p>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mt-6 mb-2">Files</div>
        {files.length === 0 && <div className="text-white/40 text-sm">No files.</div>}
        <ul className="space-y-2">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between border border-white/10 p-3">
              <div className="flex items-center gap-3 min-w-0"><FileText size={16} className="text-[#2A5BFF]" /><span className="truncate text-sm">{f.original_name}</span></div>
              <button onClick={() => downloadCustomerFile(f.filename, f.original_name)} className="btn-ghost px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase" data-testid={`coach-file-download-${f.id}`}><Download size={12} className="inline mr-1" />Download</button>
            </li>
          ))}
        </ul>
      </section>

      {/* Universal Review Workspace */}
      <section className="mt-10" data-testid="coach-universal-review">
        <h2 className="font-anton uppercase text-2xl mb-4">Your review</h2>
        <Field label="Overall Assessment *" value={form.overall_assessment} onChange={(v) => set("overall_assessment", v)} disabled={completed} testid="review-overall" />
        <Field label="What's Going Well" value={form.going_well} onChange={(v) => set("going_well", v)} disabled={completed} testid="review-going-well" />
        <Field label="What Needs Improving" value={form.needs_improving} onChange={(v) => set("needs_improving", v)} disabled={completed} testid="review-needs-improving" />
        <Field label="Recommendations" value={form.recommendations} onChange={(v) => set("recommendations", v)} disabled={completed} testid="review-recommendations" />

        <div className="mt-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Top 3 Priorities</label>
          {form.top_priorities.map((p, i) => (
            <input key={i} type="text" className="input-line focus-ring" value={p} onChange={(e) => setPriority(i, e.target.value)} disabled={completed} placeholder={`Priority ${i + 1}`} data-testid={`review-priority-${i}`} />
          ))}
        </div>

        <Field label="Additional Notes" value={form.additional_notes} onChange={(v) => set("additional_notes", v)} disabled={completed} testid="review-additional" rows={4} />

        <div className="mt-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Attachment (optional)</label>
          {!completed && (
            <div className="mt-2">
              <label className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase cursor-pointer">
                <Upload size={14} /> Upload
                <input type="file" className="hidden" onChange={attach} data-testid="review-attach" />
              </label>
            </div>
          )}
          {order.review_file && (
            <div className="mt-3 border border-white/10 p-3 flex items-center justify-between">
              <div className="flex items-center gap-3"><FileText size={16} className="text-[#2A5BFF]" /><span className="text-sm">{order.review_file.original_name}</span></div>
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">{(order.review_file.size/1024).toFixed(1)} KB</span>
            </div>
          )}
        </div>

        {!completed && (
          <div className="mt-8 flex flex-wrap gap-3 border-t border-white/10 pt-6">
            <button onClick={saveDraft} disabled={saving} className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="review-save-draft">
              <Save size={14} /> {saving ? "Saving…" : "Save Draft"}
            </button>
            <button onClick={submit} disabled={submitting} className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.24em] uppercase" data-testid="review-submit">
              <Send size={14} /> {submitting ? "Submitting…" : "Submit Review"}
            </button>
          </div>
        )}
        {completed && (
          <div className="mt-8 border border-[#2A5BFF]/60 bg-[#2A5BFF]/[0.05] p-5 text-white/80" data-testid="coach-review-completed">
            Review submitted on {new Date(order.completed_at).toLocaleString()} — visible to the customer now.
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, disabled, testid, rows = 3 }) {
  return (
    <div className="mt-6">
      <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">{label}</label>
      <textarea rows={rows} className="input-line focus-ring resize-none w-full" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} />
    </div>
  );
}
